'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Image from 'next/image';
import { Menu, X, Search, LogOut, User, Brain, Building2, Bell, UserCircle, Sparkles, MessageSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const navLinks = [
  { label: 'Inicio', href: '/' },
  { label: 'Proveedores', href: '/search?q=' },
  { label: 'Categorías', href: '/#categories' },
  { label: 'Mi Panel', href: '/buyer-dashboard' },
];

interface NotificationItem {
  id: string;
  type: 'rfq' | 'workspace';
  title: string;
  body: string;
  created_at: string;
  is_read: boolean;
  link: string;
  db_id: string; // actual DB row id for persisting is_read
  source: 'rfq_notifications' | 'workspace_notifications';
}

export default function Topbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, signOut, canSell, profile } = useAuth();
  const supabase = createClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'normal' | 'ai'>('normal');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Notifications state
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const desktopNotifRef = useRef<HTMLDivElement>(null);
  const mobileNotifRef = useRef<HTMLDivElement>(null);
  const [notifPulse, setNotifPulse] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [searchOpen]);

  // Close notif panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedDesktop = desktopNotifRef.current?.contains(target);
      const clickedMobile = mobileNotifRef.current?.contains(target);
      if (!clickedDesktop && !clickedMobile) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load notifications when user is available
  useEffect(() => {
    if (!user) { setNotifications([]); return; }
    loadNotifications();

    // Polling fallback every 15 seconds
    const pollInterval = setInterval(() => {
      loadNotifications();
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [user]);

  // Real-time subscriptions on rfq_notifications and workspace_notifications
  useEffect(() => {
    if (!user) return;

    const rfqNotifChannel = supabase
      .channel(`topbar-rfq-notif-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'rfq_notifications',
        filter: `recipient_id=eq.${user.id}`,
      }, async (payload) => {
        const row = payload.new as any;
        // Fetch the rfq title
        const { data: rfq } = await supabase
          .from('rfqs')
          .select('id, title, description')
          .eq('id', row.rfq_id)
          .maybeSingle();

        const notif = buildRfqNotification(row, rfq);
        if (notif) {
          setNotifications(prev => [notif, ...prev].slice(0, 20));
          setNotifPulse(true);
          setTimeout(() => setNotifPulse(false), 3000);
        }
      })
      .subscribe();

    const wsNotifChannel = supabase
      .channel(`topbar-ws-notif-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'workspace_notifications',
        filter: `recipient_id=eq.${user.id}`,
      }, (payload) => {
        const row = payload.new as any;
        const notif = buildWsNotification(row);
        if (notif) {
          setNotifications(prev => [notif, ...prev].slice(0, 20));
          setNotifPulse(true);
          setTimeout(() => setNotifPulse(false), 3000);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(rfqNotifChannel);
      supabase.removeChannel(wsNotifChannel);
    };
  }, [user]);

  const buildRfqNotification = (row: any, rfq: any): NotificationItem | null => {
    const rfqId = row.rfq_id;
    const rfqTitle = rfq?.title || rfq?.description?.slice(0, 60) || 'Solicitud de cotización';
    let title = '';
    let body = '';
    let link = '';

    switch (row.notification_type) {
      case 'new_rfq':
        title = '📋 Nueva solicitud de cotización';
        body = rfqTitle;
        link = `/buyer-dashboard?mode=seller&tab=messages`;
        break;
      case 'rfq_viewed':
        title = '👁️ Tu solicitud fue vista';
        body = rfqTitle;
        link = `/buyer-dashboard?mode=buyer&tab=messages`;
        break;
      case 'quotation_received':
        title = '💰 Cotización recibida';
        body = rfqTitle;
        link = `/buyer-dashboard?mode=buyer&tab=messages`;
        break;
      case 'rfq_rejected':
        title = '❌ Solicitud rechazada';
        body = rfqTitle;
        link = `/buyer-dashboard?mode=buyer&tab=messages`;
        break;
      default:
        title = '📋 Notificación de solicitud';
        body = rfqTitle;
        link = `/buyer-dashboard?mode=buyer&tab=messages`;
    }

    return {
      id: `rfqn-${row.id}`,
      db_id: row.id,
      type: 'rfq',
      source: 'rfq_notifications',
      title,
      body,
      created_at: row.created_at || new Date().toISOString(),
      is_read: row.is_read || false,
      link,
    };
  };

  const buildWsNotification = (row: any): NotificationItem | null => {
    const wsId = row.workspace_id;
    const notifType = row.notification_type || '';
    // For new_message notifications, send buyer to their messages inbox
    // For other workspace notifications (invitations, status changes), go to workspace directly
    let link = notifType === 'new_message'
      ? `/buyer-dashboard?tab=messages`
      : `/workspace/${wsId}`;
    return {
      id: `wsn-${row.id}`,
      db_id: row.id,
      type: 'workspace',
      source: 'workspace_notifications',
      title: row.title || '💬 Notificación de workspace',
      body: row.body || '',
      created_at: row.created_at || new Date().toISOString(),
      is_read: row.is_read || false,
      link,
    };
  };

  const loadNotifications = async () => {
    if (!user) return;
    const notifList: NotificationItem[] = [];

    // Load rfq_notifications for this user
    const { data: rfqNotifs } = await supabase
      .from('rfq_notifications')
      .select('id, rfq_id, notification_type, is_read, created_at')
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .limit(15);

    if (rfqNotifs && rfqNotifs.length > 0) {
      // Fetch rfq titles in one query
      const rfqIds = [...new Set(rfqNotifs.map((n: any) => n.rfq_id))];
      const { data: rfqs } = await supabase
        .from('rfqs')
        .select('id, title, description')
        .in('id', rfqIds);

      const rfqMap: Record<string, any> = {};
      (rfqs || []).forEach((r: any) => { rfqMap[r.id] = r; });

      rfqNotifs.forEach((row: any) => {
        const notif = buildRfqNotification(row, rfqMap[row.rfq_id]);
        if (notif) notifList.push(notif);
      });
    }

    // Load workspace_notifications for this user
    const { data: wsNotifs } = await supabase
      .from('workspace_notifications')
      .select('id, workspace_id, notification_type, title, body, is_read, created_at')
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    (wsNotifs || []).forEach((row: any) => {
      const notif = buildWsNotification(row);
      if (notif) notifList.push(notif);
    });

    // Sort combined list by date desc
    notifList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setNotifications(notifList.slice(0, 20));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const persistMarkRead = async (dbId: string, source: 'rfq_notifications' | 'workspace_notifications') => {
    await supabase
      .from(source)
      .update({ is_read: true })
      .eq('id', dbId);
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.is_read);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    // Persist each unread
    for (const n of unread) {
      await persistMarkRead(n.db_id, n.source);
    }
  };

  const handleNotifClick = async (notif: NotificationItem) => {
    // Mark as read in state
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
    setNotifOpen(false);
    // Persist is_read
    await persistMarkRead(notif.db_id, notif.source);
    // Navigate
    router.push(notif.link);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchOpen(false);
      if (searchMode === 'ai') {
        router.push(`/buyer-dashboard?tab=ai-sourcing&q=${encodeURIComponent(searchQuery.trim())}`);
      } else {
        router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
      setSearchQuery('');
      setSearchMode('normal');
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router?.push('/');
      router?.refresh();
    } catch (err) {
      // silent
    } finally {
      setSigningOut(false);
    }
  };

  // Check admin via app_metadata (server-controlled, not user_metadata)
  const isAdmin = !loading && user && (
    user.app_metadata?.role === 'superadmin' ||
    user.app_metadata?.role === 'admin'
  );

  const userCanSell = !loading && user && canSell();

  const formatNotifTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `hace ${diffH}h`;
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  };

  // Notification dropdown content (shared between desktop and mobile)
  const NotifDropdown = () => (
    <div className="max-h-80 overflow-y-auto">
      {notifications.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <Bell size={24} className="text-white/20 mx-auto mb-2" />
          <p className="text-xs text-white/30">Sin notificaciones nuevas</p>
        </div>
      ) : (
        notifications.map(notif => (
          <button
            key={notif.id}
            onClick={() => handleNotifClick(notif)}
            className={`block w-full text-left px-4 py-3 border-b border-white/[0.06] last:border-0 hover:bg-white/[0.04] transition-colors ${!notif.is_read ? 'bg-white/[0.03]' : ''}`}
          >
            <div className="flex items-start gap-2">
              {!notif.is_read && (
                <span className="w-2 h-2 rounded-full bg-white flex-shrink-0 mt-1.5 shadow-[0_0_6px_rgba(255,255,255,0.7)]" />
              )}
              <div className={`flex-1 min-w-0 ${notif.is_read ? 'pl-4' : ''}`}>
                <p className="text-xs font-semibold text-white/90 truncate">{notif.title}</p>
                <p className="text-xs text-white/40 mt-0.5 line-clamp-2 leading-relaxed">{notif.body}</p>
                <p className="text-[10px] text-white/25 mt-1">{formatNotifTime(notif.created_at)}</p>
              </div>
            </div>
          </button>
        ))
      )}
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md border-b border-white/10`}
      style={{ backgroundColor: scrolled || pathname !== '/' ? (scrolled ? 'rgba(0,0,0,0.97)' : 'rgba(0,0,0,0.92)') : 'transparent', borderBottomColor: scrolled || pathname !== '/' ? undefined : 'transparent' }}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0" translate="no">
              <Image
                src="/assets/images/Captura_de_pantalla_2026-08-22_a_las_4.05.56_p._m.-1788225375226.png"
                alt="MOONDEPTH logo"
                width={36}
                height={36}
                className="flex-shrink-0 object-contain"
                priority
              />
            </Link>
            <Link href="/#pricing" className="hidden xl:inline-flex items-center rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wider text-white/50 hover:bg-white/10 transition-colors">
              Gratis por lanzamiento
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0">
            {navLinks.map((link) => (
              <Link
                key={`nav-${link.href}`}
                href={link.href}
                className={`px-4 py-2 text-sm transition-colors duration-150 ${
                  pathname === link.href ? 'text-white font-medium' : 'text-white/60 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user && (
              <Link
                href="/workspace"
                className={`px-4 py-2 text-sm transition-colors duration-150 flex items-center gap-1.5 ${
                  pathname?.startsWith('/workspace') ? 'text-white font-medium' : 'text-white/60 hover:text-white'
                }`}
              >
                <MessageSquare size={13} />
                Workspaces
              </Link>
            )}
            {isAdmin && (
              <Link
                href="/creator-dashboard"
                className={`px-4 py-2 text-sm transition-colors duration-150 flex items-center gap-1.5 ${
                  pathname === '/creator-dashboard' ? 'text-white font-medium' : 'text-white/60 hover:text-white'
                }`}
              >
                <Brain size={13} />
                Admin
              </Link>
            )}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            {searchOpen ? (
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                <div className="relative">
                  {searchMode === 'ai' ? (
                    <Sparkles size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
                  ) : (
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  )}
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={e => {
                      const val = e.target.value;
                      setSearchQuery(val);
                      // Detect AI mode: conversational query > 30 chars
                      const t = val.toLowerCase().trim();
                      const signals = ['necesito', 'busco', 'quiero', 'estoy buscando', 'requiero', 'proyecto', 'fabricar', 'instalar', 'construir', 'diseñar', 'desarrollar', 'alguien que', 'empresa que', 'proveedor que'];
                      setSearchMode(t.length > 30 && signals.some(s => t.includes(s)) ? 'ai' : 'normal');
                    }}
                    placeholder={searchMode === 'ai' ? 'Describe lo que necesitas...' : 'Buscar proveedores...'}
                    className={`w-56 pl-9 pr-4 py-1.5 text-sm border rounded-xl outline-none transition-all bg-white/10 text-white placeholder-white/40 ${
                      searchMode === 'ai' ? 'border-white/40 shadow-[0_0_0_2px_rgba(255,255,255,0.06)]' : 'border-white/20 focus:border-white'
                    }`}
                    onBlur={() => { if (!searchQuery) { setSearchOpen(false); setSearchMode('normal'); } }}
                  />
                </div>
                <button
                  type="submit"
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition-all ${
                    searchMode === 'ai' ?'bg-white text-black hover:bg-white/90' :'bg-white text-black hover:bg-white/90'
                  }`}
                >
                  {searchMode === 'ai' ? (
                    <>
                      <Sparkles size={12} />
                      AI Sourcing
                    </>
                  ) : (
                    <>
                      <Search size={12} />
                      Buscar
                    </>
                  )}
                </button>
                <button type="button" onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchMode('normal'); }} className="p-1.5 text-white/60 hover:text-white transition-colors">
                  <X size={15} />
                </button>
              </form>
            ) : (
              <button onClick={() => setSearchOpen(true)} className="p-2 text-white/60 hover:text-white transition-colors duration-150" aria-label="Buscar">
                <Search size={17} />
              </button>
            )}

            {/* Desktop Notification Bell */}
            {user && (
              <div className="relative" ref={desktopNotifRef}>
                <button
                  onClick={() => setNotifOpen(o => !o)}
                  className={`relative p-2 text-white/60 hover:text-white transition-colors duration-150 ${notifPulse ? 'animate-bounce' : ''}`}
                  aria-label="Notificaciones"
                >
                  <Bell size={17} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification dropdown */}
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl shadow-2xl overflow-hidden z-50" style={{ background: '#0d0d12', border: '1px solid rgba(255,255,255,0.18)', boxShadow: '0 8px 40px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08)' }}>
                    <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <h3 className="text-sm font-bold text-white/90">Notificaciones</h3>
                      {notifications.length > 0 && (
                        <button onClick={markAllRead} className="text-xs text-white/40 hover:text-white transition-colors">
                          Marcar todo leído
                        </button>
                      )}
                    </div>
                    <NotifDropdown />
                    {notifications.length > 0 && (
                      <div className="px-4 py-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <button
                          onClick={() => { setNotifOpen(false); router.push('/buyer-dashboard?tab=messages'); }}
                          className="text-xs text-white/70 font-semibold hover:text-white transition-colors"
                        >
                          Ver todos los mensajes →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {loading ? (
              <div className="w-20 h-8 rounded-full animate-pulse bg-white/10" />
            ) : user ? (
              <div className="flex items-center gap-2">
                {userCanSell && (
                  <Link
                    href="/buyer-dashboard"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded-full transition-all"
                  >
                    <Building2 size={12} />
                    Mi Empresa
                  </Link>
                )}
                <Link
                  href="/profile"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition-all"
                >
                  <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                    {profile?.logo_url ? (
                      <img src={profile.logo_url} alt="Logo empresa" className="w-full h-full object-contain p-0.5" />
                    ) : (
                      <User size={11} className="text-black" />
                    )}
                  </div>
                  <span className="text-xs font-medium text-white max-w-[120px] truncate">
                    {user?.user_metadata?.full_name?.split(' ')?.[0] || user?.email?.split('@')?.[0] || 'Mi cuenta'}
                  </span>
                </Link>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white/60 hover:text-white border border-white/20 hover:border-white rounded-full transition-all duration-150 disabled:opacity-50"
                  title="Cerrar sesión"
                >
                  {signingOut ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <LogOut size={14} />
                  )}
                  <span className="hidden xl:inline text-xs">Salir</span>
                </button>
              </div>
            ) : (
              <>
                <Link href="/sign-up-login" className="px-4 py-1.5 text-sm text-white/60 hover:text-white transition-colors duration-150">
                  Iniciar sesión
                </Link>
                <Link href="/sign-up-login" className="px-4 py-1.5 text-sm font-medium bg-white text-black rounded-full hover:bg-white/90 transition-all duration-150 active:scale-95">
                  Registra tu negocio
                </Link>
              </>
            )}
          </div>

          {/* Mobile: Bell + hamburger */}
          <div className="lg:hidden flex items-center gap-1">
            {user && (
              <div className="relative" ref={mobileNotifRef}>
                <button
                  onClick={() => setNotifOpen(o => !o)}
                  className={`relative p-2 text-white/60 hover:text-white transition-colors ${notifPulse ? 'animate-bounce' : ''}`}
                  aria-label="Notificaciones"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 rounded-2xl shadow-2xl overflow-hidden z-50" style={{ background: '#0d0d12', border: '1px solid rgba(255,255,255,0.18)', boxShadow: '0 8px 40px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08)' }}>
                    <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <h3 className="text-sm font-bold text-white/90">Notificaciones</h3>
                      {notifications.length > 0 && (
                        <button onClick={markAllRead} className="text-xs text-white/40 hover:text-white">Leer todo</button>
                      )}
                    </div>
                    <NotifDropdown />
                  </div>
                )}
              </div>
            )}
            <button className="p-2 text-white/60 hover:text-white transition-colors" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menú">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-b border-white/10 animate-fade-in bg-black">
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link href="/#pricing" onClick={() => setMobileOpen(false)} className="mb-2 rounded-xl border border-white/12 bg-white/5 px-4 py-2.5 text-xs font-semibold text-white/60">
              Gratis durante el período de lanzamiento · después habrá planes de pago
            </Link>
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Buscar proveedores..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-white/20 rounded-xl outline-none focus:border-white transition-all bg-white/10 text-white placeholder-white/40"
                />
              </div>
              <button type="submit" className="px-3 py-2 bg-white text-black text-xs font-medium rounded-xl">Buscar</button>
            </form>
            {navLinks.map((link) => (
              <Link
                key={`mobile-nav-${link.href}`}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm text-white/60 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {user && (
              <Link
                href="/workspace"
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2"
              >
                <MessageSquare size={14} />
                Workspaces
              </Link>
            )}
            {isAdmin && (
              <Link
                href="/creator-dashboard"
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2"
              >
                <Brain size={14} />
                Admin
              </Link>
            )}
            <div className="border-t border-white/10 mt-3 pt-3 flex flex-col gap-2">
              {user ? (
                <>
                  <div className="px-4 py-2 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                      <User size={12} className="text-black" />
                    </div>
                    <span className="text-sm font-medium text-white truncate">
                      {user?.user_metadata?.full_name || user?.email}
                    </span>
                  </div>
                  <Link
                    href="/profile"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/60 hover:text-white transition-colors"
                  >
                    <UserCircle size={14} />
                    Mi Perfil
                  </Link>
                  <button
                    onClick={() => { setMobileOpen(false); handleSignOut(); }}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/60 hover:text-white transition-colors"
                  >
                    <LogOut size={14} />
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <>
                  <Link href="/sign-up-login" onClick={() => setMobileOpen(false)} className="px-4 py-2.5 text-sm text-white/60 hover:text-white transition-colors">
                    Iniciar sesión
                  </Link>
                  <Link href="/sign-up-login" onClick={() => setMobileOpen(false)} className="mx-4 py-2.5 text-sm font-medium bg-white text-black rounded-xl text-center hover:bg-white/90 transition-all">
                    Registra tu negocio
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
